import type { Profile } from "@/hooks/useAuth";

/**
 * Lo stato di accesso dell'account, calcolato dal profilo.
 *
 * E' la COPIA per l'interfaccia della logica che vive nella edge function
 * `chat` (statoAccesso): il server decide, questo serve solo a mostrare le
 * cose giuste - popup, banner, campo disabilitato - senza aspettare un 403.
 * Se i due divergono comanda il server; i numeri stanno in un posto solo per
 * parte (PROVA_GIORNI qui e la' devono coincidere, ed e' scritto su entrambi).
 */
export const PROVA_GIORNI = 7;

export type TipoAccount = "prova" | "attivo" | "scaduto";

export interface StatoAccount {
  tipo: TipoAccount;
  /** Giorni interi rimanenti (di prova o di abbonamento); undefined se senza scadenza. */
  giorniRimanenti?: number;
  /**
   * Solo per «scaduto»: era un ABBONATO (aveva i marchi) e la data e' passata.
   * Dal 07/09/2026 cambia le parole in tutta l'app: a chi ha pagato si dice
   * «rinnova», non «il periodo di prova e' terminato». Stessa distinzione del
   * server (abbonamento_scaduto).
   */
  abbonamentoScaduto?: { il: string | null; marchi: string[] };
}

export function statoAccount(profile: Profile | null): StatoAccount | null {
  if (!profile) return null;

  const brands = profile.brands ?? [];
  const scadenza = profile.scadenza ? Date.parse(profile.scadenza) : null;
  const scadenzaPassata = scadenza !== null && !Number.isNaN(scadenza) && scadenza <= Date.now();

  if (brands.length > 0 && !scadenzaPassata) {
    return {
      tipo: "attivo",
      giorniRimanenti: scadenza === null || Number.isNaN(scadenza)
        ? undefined
        : Math.max(0, Math.ceil((scadenza - Date.now()) / 86_400_000)),
    };
  }

  // Aveva i marchi e la data e' passata: cliente scaduto, non prova finita.
  // La prova non si riapre a chi ha gia' avuto l'abbonamento (come sul server).
  if (brands.length > 0 && scadenzaPassata) {
    return { tipo: "scaduto", abbonamentoScaduto: { il: profile.scadenza ?? null, marchi: brands } };
  }

  const inizio = Date.parse(profile.created_at ?? "");
  // Senza una data d'inizio leggibile NON si dichiara la prova finita: questo
  // stato accende il popup e spegne il campo delle domande, cioe' blocca
  // l'app. Il profilo si legge con select(*) e la colonna potrebbe mancare o
  // arrivare vuota; in quel caso decide il server, che davanti a un dubbio
  // lascia lavorare col tetto della prova invece di sbarrare la porta a un
  // cliente pagante.
  if (Number.isNaN(inizio)) return { tipo: "prova" };

  const fine = inizio + PROVA_GIORNI * 86_400_000;
  if (Date.now() < fine) {
    return {
      tipo: "prova",
      giorniRimanenti: Math.max(0, Math.ceil((fine - Date.now()) / 86_400_000)),
    };
  }

  return { tipo: "scaduto" };
}
